// Last-known values for a few NON-SENSITIVE settings, kept in localStorage so
// the first frame after a launch is already laid out correctly (IMP-062).
//
// Since the app renders from the persisted session (lib/persistedSession.ts),
// the Home page draws seconds before Firestore can answer — Firestore waits for
// Firebase Auth to finish starting up. Without these values, every launch drew
// the default tile set and then reshuffled it when the real settings arrived.
//
// What may go in here: settings whose disclosure harms no one — the org-wide
// feature flags (which modules are switched on; the config/features document
// is readable by every signed-in account) and the signed-in user's own
// Home-section show/hide toggles. Nothing else. Keeping copies of Class 1
// business data (directory, announcements, events, doors) on the device is a
// data-at-rest decision for InfraSec, tracked in docs/OPS-CHECKLIST.md — do not
// widen this module without that sign-off.
//
// Every access is wrapped: storage can throw in some privacy modes, and a
// missing or corrupt entry must simply mean "no last-known value".

const PREFIX = 'onlogic:lastKnown:v1:';

/** Read a stored value through a validator; null when absent, corrupt or invalid. */
export function readLastKnown<T>(key: string, parse: (raw: unknown) => T | null): T | null {
  try {
    const stored = localStorage.getItem(PREFIX + key);
    return stored === null ? null : parse(JSON.parse(stored));
  } catch {
    return null;
  }
}

/** Store a value; failures (quota, privacy mode) are ignored — it is only a hint. */
export function writeLastKnown(key: string, value: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Best-effort by design.
  }
}

/** Remove every last-known value (on sign-out). */
export function clearLastKnown(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Best-effort by design.
  }
}

/** Validator for a flat string → boolean map (e.g. Home-section toggles). */
export function parseBooleanMap(raw: unknown): Record<string, boolean> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}
